const LAST_ACTIVITY_STORAGE_KEY_PREFIX = 'professional.lastActivity'

type UserId = string | number | null | undefined

const storageKey = (userId: UserId): string | null => {
  if (userId === null || userId === undefined || userId === '') return null
  return `${LAST_ACTIVITY_STORAGE_KEY_PREFIX}.${userId}`
}

/**
 * localStorage throws rather than returning null in Safari private mode, when
 * storage is disabled by policy, and on quota errors. Idle detection must
 * degrade to in-memory tracking in those cases, never fail to arm.
 */
const withStorage = <T>(fn: (storage: Storage) => T, fallback: T): T => {
  if (typeof window === 'undefined') return fallback
  try {
    return fn(window.localStorage)
  } catch {
    return fallback
  }
}

export const readPersistedLastActivity = (userId: UserId): number | null => {
  const key = storageKey(userId)
  if (!key) return null

  return withStorage((storage) => {
    const raw = storage.getItem(key)
    if (raw === null) return null

    // Number() rather than parseInt(): parseInt accepts '1700000000000junk'
    // and '1700000000000.1', so a corrupted value could still defer logout.
    const parsed = Number(raw)
    if (!Number.isSafeInteger(parsed) || parsed <= 0) return null

    // A future timestamp means a tampered or clock-skewed value; treat it as
    // absent rather than letting it defer the logout indefinitely.
    if (parsed > Date.now()) return null

    return parsed
  }, null)
}

export const writePersistedLastActivity = (userId: UserId, value: number): void => {
  const key = storageKey(userId)
  if (!key) return

  withStorage((storage) => storage.setItem(key, String(value)), undefined)
}

export const clearPersistedLastActivity = (userId: UserId): void => {
  const key = storageKey(userId)
  if (!key) return

  withStorage((storage) => storage.removeItem(key), undefined)
}

/**
 * Clears every user's stamp.
 *
 * resetAuthUser() also runs when fetchAuthUser() fails, at which point there is
 * no in-memory user to key on — a stale stamp would then survive and log the
 * same user straight back out on their next login.
 */
export const clearAllPersistedLastActivity = (): void => {
  withStorage((storage) => {
    const keys: string[] = []
    for (let i = 0; i < storage.length; i += 1) {
      const key = storage.key(i)
      if (key?.startsWith(`${LAST_ACTIVITY_STORAGE_KEY_PREFIX}.`)) keys.push(key)
    }
    keys.forEach((key) => storage.removeItem(key))
  }, undefined)
}
